import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from "react";
import { useSession } from "@descope/react-sdk";
import { loadRumClient } from "./rumClient.js";
import { openUsageRecording, recordScreenView, setUsageAttributes, startUsageClient, stopUsageRecording } from "./track.js";
const SUPER_ADMIN_ROLE = "super-admin";
// Read from the token itself: the SDK's `claims` are not always populated.
export function sessionFacts(sessionToken) {
    const payload = sessionToken?.split(".")[1];
    if (!payload)
        return { superAdmin: false };
    try {
        const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "=")));
        return {
            userId: typeof claims.sub === "string" ? claims.sub : undefined,
            superAdmin: Array.isArray(claims.roles) && claims.roles.includes(SUPER_ADMIN_ROLE),
            enteredTenantId: typeof claims.admin?.tenantId === "string" ? claims.admin.tenantId : undefined,
        };
    }
    catch {
        return { superAdmin: false };
    }
}
/** The attributes on every record (design D6): staff is a super-admin, or a caller who entered this tenant. */
export function usageAttributes(app, facts, scope) {
    const attributes = {
        app,
        staff: facts.superAdmin || (scope.tenantId !== undefined && facts.enteredTenantId === scope.tenantId),
    };
    if (facts.userId)
        attributes.userId = facts.userId;
    if (scope.tenantId)
        attributes.tenantId = scope.tenantId;
    if (scope.organizationId)
        attributes.organizationId = scope.organizationId;
    if (scope.accessLevel)
        attributes.accessLevel = scope.accessLevel;
    return attributes;
}
// One client per page, however often the provider mounts (StrictMode, remounts).
let starting;
// The user the client last recorded for: a different one signing in gets a session of their own.
let recordedUser;
export function resetUsageProviderForTests() {
    starting = undefined;
    recordedUser = undefined;
}
/**
 * Records the signed-in user's usage of this app (spec frontend-usage-tracking): starts the recording
 * client once a session exists, keeps its attributes and credentials current, and records a screen view
 * per path under the route's template.
 */
export function UsageTrackingProvider({ app, config, tenantId, organizationId, accessLevel, router, loadClient = loadRumClient, children }) {
    const { sessionToken, isAuthenticated } = useSession();
    const signedIn = Boolean(isAuthenticated && sessionToken);
    const facts = useMemo(() => sessionFacts(sessionToken), [sessionToken]);
    const attributes = useMemo(() => usageAttributes(app, facts, { tenantId, organizationId, accessLevel }), [app, facts, tenantId, organizationId, accessLevel]);
    const configured = Boolean(config.rumAppMonitorId && config.identityPoolId);
    // A ref, so a re-run of the screen-view effect (StrictMode, a new router) does not record the same screen again.
    const lastPath = useRef(undefined);
    const latest = useRef({ attributes, sessionToken, signedIn });
    latest.current = { attributes, sessionToken, signedIn };
    useEffect(() => {
        if (!configured) {
            stopUsageRecording();
            return;
        }
        if (!signedIn || !sessionToken) {
            // Signed out without a reload: a client that recorded the previous session must stop too, since its
            // own error and performance collection would otherwise carry that user's attributes on.
            stopUsageRecording();
            lastPath.current = undefined;
            void starting?.then((client) => client?.disable());
            return;
        }
        openUsageRecording();
        starting ??= loadClient({
            appMonitorId: config.rumAppMonitorId,
            identityPoolId: config.identityPoolId,
            region: config.region ?? "eu-west-1",
            loginKey: `${new URL(config.authBaseUrl).host}/v1/apps/${config.descopeProjectId}`,
            sessionToken,
            attributes: latest.current.attributes,
        }).catch(() => undefined);
        void starting.then((client) => {
            try {
                if (!client) {
                    stopUsageRecording();
                    return;
                }
                // The session may have ended, or its token been refreshed, while the client loaded.
                if (!latest.current.signedIn || !latest.current.sessionToken) {
                    client.disable();
                    return;
                }
                const user = latest.current.attributes.userId;
                if (recordedUser !== undefined && user !== recordedUser)
                    client.startSession();
                recordedUser = user;
                client.setSessionToken(latest.current.sessionToken);
                client.addSessionAttributes(latest.current.attributes);
                client.enable();
                startUsageClient(client);
            }
            catch {
                stopUsageRecording();
            }
        });
    }, [configured, signedIn, sessionToken, config, loadClient]);
    useEffect(() => {
        if (signedIn)
            setUsageAttributes(attributes);
    }, [signedIn, attributes]);
    useEffect(() => {
        if (!router || !signedIn)
            return;
        const recordCurrent = () => {
            const path = router.state.location.pathname;
            if (path === lastPath.current)
                return;
            const routeId = router.state.matches.at(-1)?.routeId;
            if (!routeId)
                return;
            lastPath.current = path;
            recordScreenView(routeId, path);
        };
        recordCurrent();
        return router.subscribe("onResolved", recordCurrent);
    }, [router, signedIn]);
    // The CMS unmounts the provider on sign-out rather than showing it a signed-out session. A remount
    // (StrictMode, signing in again) records the current screen afresh.
    useEffect(() => () => {
        stopUsageRecording();
        lastPath.current = undefined;
        void starting?.then((client) => client?.disable());
    }, []);
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=UsageTrackingProvider.js.map